/* MA-8 in the demo. The demo journey stands in for the player, so it keeps here what the player's test link sends
   the API (`BMR-Test`): the test link opened (`scan`) and the test participation (`play`, with its gift). It is kept
   in this browser, per world and per campaign, and the mock's GET /app/campaigns/:id/test reads it, as the API reads
   the player's rows. A test counts nowhere else. `validated` is the till's, in mode test: the real till
   (go…/caisse#mode=test) is out of the demo's reach, so the demo never sets it.
   Per-visit ruling P6 (rule 8) · a test link carrying `visit=N` plays that visit: its play is kept per position too, with the
   version it played and the visit's content key (lib/campaign › visitKeyOf), so the mock's `visits[]` can say « À retester »
   when the content changed. Visit 1's play is the campaign's record as well (a tester's phone has no history). */
import type { CampaignTest } from '@/lib/api'
import { currentWorld } from '@/mocks/worlds'

const key = () => `bmr.test.${currentWorld()}`
const NONE: Pick<CampaignTest, 'scan' | 'play' | 'validated'> = { scan: null, play: null, validated: null }
/** One visit's latest test (P6): its play, the till's validation, the version and content key it played. */
export interface VisitTestRecord { play: CampaignTest['play']; validated: CampaignTest['validated']; version: number | null; key: string | null }
type Stored = Partial<Pick<CampaignTest, 'scan' | 'play' | 'validated'>> & { visits?: Record<string, VisitTestRecord> }

function readAll(): Record<string, Stored> {
  try { const m = JSON.parse(localStorage.getItem(key()) ?? '{}'); return m && typeof m === 'object' ? m : {} } catch { return {} }
}
/** The test read of one campaign, as the API answers it: three nulls before any test (the per-visit rows are the mock's to compose). */
export const readTest = (campaignId: string): CampaignTest => { const { visits: _v, ...rest } = readAll()[campaignId] ?? {}; return { ...NONE, ...rest } }
/** The per-visit records of one campaign, by position (« 1 », « 2 », …). */
export const readVisitTests = (campaignId: string): Record<string, VisitTestRecord> => readAll()[campaignId]?.visits ?? {}
/** A test event of the demo journey. A new play is a new gift: its validation starts over, as in the API. With `visit`, the play
    is the visit's record too (position, version, content key). */
export function recordTest(campaignId: string, event: { scan: true } | { play: CampaignTest['play'] }, visit?: { position: number; version: number; key: string }): void {
  const all = readAll()
  const at = new Date().toISOString()
  const cur = all[campaignId] ?? {}
  if ('scan' in event) all[campaignId] = { ...NONE, ...cur, scan: { at } }
  else {
    const visits = visit ? { ...cur.visits, [String(visit.position)]: { play: event.play, validated: null, version: visit.version, key: visit.key } } : cur.visits
    const own = !visit || visit.position === 1
    all[campaignId] = { ...NONE, ...cur, ...(own ? { play: event.play, validated: null } : {}), ...(visits ? { visits } : {}) }
  }
  try { localStorage.setItem(key(), JSON.stringify(all)) } catch { /* fine */ }
}
/** The server's id replaces the draft's at its first publish (publish wiring): the campaign's test events follow it. */
export function rekeyTest(from: string, to: string): void {
  const all = readAll()
  if (from === to || !(from in all) || to in all) return
  all[to] = all[from]; delete all[from]
  try { localStorage.setItem(key(), JSON.stringify(all)) } catch { /* fine */ }
}
